import { acceptHMRUpdate, defineStore } from "pinia";
import { useNow } from "@vueuse/core";
import { type Ref, computed, ref, watch } from "vue";

import { useIdentityStore } from "@/data/stores/identity";
import { useTableAllocationStore } from "@/data/stores/tableAllocation";

import { Factory } from "@/data/dao/Factory";
import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import useAppSocket from "@/data/dao/socket/composables/appSocket";
import type { IBillItem, IBillItemOption, ISelectedItem } from "@/data/model/Bill";
import { Bill, type IBill, type IUserItemSelection, type PaidLineItem, type UnpaidLineItem } from "@/data/model/Bill";
import { exceptionTypes } from "@/data/util/Config";
import { applyTimerDiscounts, calculateTimerPrice, getTimerElapsedSeconds } from "@/data/util/TimerPrice";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";

import { Popup } from "@/components/dialog/Popup";

import { trans } from "@/operator/localization/Trans";

type BillSelectionTotals = {
    selectionDiscountAmount: number;
    selectionTotalAfterItemDiscounts: number;
    selectionTotalDiscountsAndVouchersAmount: number;
    selectionTotalAfterDiscount: number;
};

export const useBillStore = defineStore("bill", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const identity = useIdentityStore();
    const allocation = useTableAllocationStore();

    // state
    const isLoading = ref<boolean>(false);
    const isUpdating = ref<boolean>(false);
    const currentBill = ref<IBill | null>(null);
    const unpaidItems = ref<UnpaidLineItem[]>([]) as Ref<UnpaidLineItem[]>;
    const paidItems = ref<PaidLineItem[]>([]) as Ref<PaidLineItem[]>;

    // getters
    const totalItemsSelected = computed(() => {
        let totalItems = 0;
        for (const selectedItem of selectedUnpaidItems.value) {
            totalItems += selectedItem.selectedQuantity;
        }
        return totalItems;
    });

    const locationId = computed(() => allocation.locationId);
    const consumption = computed(() => getSelectedItemsOriginalConsumption(selectedUnpaidItems.value));
    const totalPayableAfterDiscount = computed(
        () => selectionTotals.value.selectionTotalAfterDiscount + selectedTimersPriceGrowth.value
    );
    const totalDiscountsAndVouchersAmount = computed(
        () => selectionTotals.value.selectionTotalDiscountsAndVouchersAmount
    );

    const hasDiscounts = computed(() => currentBill.value != null && currentBill.value?.hasDiscount === true);

    const totalPaid = computed(() => currentBill.value?.totalPaid ?? 0);

    const selectedUnpaidItems = computed(() => unpaidItems.value.filter((item) => item.selectedQuantity > 0));

    // the price of a running timer is a snapshot of the server, which moves along with the clock of the timer on the
    // device. the clock only runs while there is a timer to price
    const timersNow = useNow({ interval: 1000, controls: true });
    const hasRunningTimers = computed(() => unpaidItems.value.some(isRunningTimerItem));
    watch(
        hasRunningTimers,
        (hasRunning) => {
            if (!hasRunning) {
                timersNow.pause();
                return;
            }

            // it stood still while no timer ran, so it is set before it goes on
            timersNow.now.value = new Date();
            timersNow.resume();
        },
        { immediate: true }
    );
    const selectedTimersPriceGrowth = computed(() => {
        let growth = 0;
        for (const selectedItem of selectedUnpaidItems.value.filter(isRunningTimerItem)) {
            // the selection total is after discounts, so is the growth: the options and the bill-level discounts do not move
            growth +=
                selectedItem.selectedQuantity *
                (getLiveUnitPriceAfterDiscount(selectedItem) - selectedItem.unitPriceAfterDiscount);
        }

        return growth;
    });
    const hasUnpaidItems = computed(() => unpaidItems.value.length > 0);
    const hasPaidItems = computed(() => paidItems.value.length > 0);
    const isAnyItemSelected = computed(() => selectedUnpaidItems.value.length > 0);
    const isOpenBill = computed(() => currentBill.value != null);
    const isFullyPaid = computed(() => {
        if (currentBill.value == null) return false;
        return currentBill.value!.totalUnpaidConsumption == 0;
    });
    const isFullyCoveredByDiscounts = computed(
        () =>
            currentBill.value != null &&
            totalDiscountsAndVouchersAmount.value > 0 &&
            consumption.value == totalDiscountsAndVouchersAmount.value
    );
    const hasVouchers = computed(() => currentBill.value?.selectionHasVoucher ?? false);

    // collection of items to update on the server
    const selectedItemsList = computed(() => {
        const selectedItems = [] as ISelectedItem[];
        for (const selectedItem of selectedUnpaidItems.value) {
            selectedItems.push({
                billItemId: selectedItem.id,
                quantity: selectedItem.selectedQuantity,
            });
        }

        return selectedItems;
    });

    const selectionTotals = computed<BillSelectionTotals>(() => {
        let selectionDiscountAmount = 0;
        let selectionTotalAfterItemDiscounts = 0;
        let selectionTotalDiscountsAndVouchersAmount = 0;
        let selectionTotalAfterDiscount = 0;

        for (const selectedItem of selectedUnpaidItems.value) {
            const selectedQuantityToAmountMap = currentBill.value?.selectedQuantityToAmountMap.get(selectedItem.id);
            if (!selectedQuantityToAmountMap) continue;

            const totalsForSelectedQuantity = selectedQuantityToAmountMap.find(
                (x) => x.selectedQuantity === selectedItem.selectedQuantity
            );
            if (!totalsForSelectedQuantity) continue;

            selectionDiscountAmount += totalsForSelectedQuantity.billDiscountsAndVouchersAmount;
            selectionTotalAfterItemDiscounts += totalsForSelectedQuantity.totalAfterItemDiscounts;
            selectionTotalDiscountsAndVouchersAmount += totalsForSelectedQuantity.totalDiscountsAndVouchersAmount;
            selectionTotalAfterDiscount += totalsForSelectedQuantity.totalAfterBillDiscountsAndVouchers;
        }

        return {
            selectionDiscountAmount,
            selectionTotalAfterItemDiscounts,
            selectionTotalDiscountsAndVouchersAmount,
            selectionTotalAfterDiscount,
        };
    });

    const unpaidAfterDiscounts = computed<number>(() => {
        let unpaidTotalAfterDiscount = 0;

        for (const unpaidItem of unpaidItems.value) {
            const selectedQuantityToAmountMap = currentBill.value?.selectedQuantityToAmountMap.get(unpaidItem.id);
            if (!selectedQuantityToAmountMap) continue;

            const totalsForUnpaidQuantity = selectedQuantityToAmountMap.find(
                (x) => x.selectedQuantity === unpaidItem.unpaidQuantity
            );
            if (!totalsForUnpaidQuantity) continue;

            unpaidTotalAfterDiscount += totalsForUnpaidQuantity.totalAfterBillDiscounts;
        }

        return unpaidTotalAfterDiscount;
    });

    const totalUnpaidConsumption = computed<number>(() => {
        return currentBill.value?.totalUnpaidConsumption ?? 0;
    });

    function getAppliedBillDiscount(discountId: string): number {
        const appliedBillDiscounts = currentBill.value?.appliedBillDiscountSummaries ?? [];
        return appliedBillDiscounts?.find((x) => x.discountId === discountId)?.appliedAmount ?? 0;
    }

    // actions
    async function loadCurrentBill(): Promise<void> {
        isLoading.value = true;

        console.log("Loading current bill...");

        const currentOrderBillId = allocation.allocation?.orderBillId;
        try {
            const currentBill = await customerDao.getCurrentBill({ billId: currentOrderBillId });
            setCurrentBill(currentBill ?? null);
        } catch (ex) {
            logError(ex);
            const e = ex as { type: string };
            if (
                e.type === exceptionTypes.userNotAtTableException ||
                e.type === exceptionTypes.billDoesNotExistException
            ) {
                setCurrentBill(null);
            }

            throw e;
        } finally {
            isLoading.value = false;
        }
    }

    async function updateBillTotals(): Promise<void> {
        const currentOrderBillId = allocation.allocation?.orderBillId;
        try {
            if (currentBill.value) {
                currentBill.value.selectionDiscountAmount = selectionTotals.value.selectionDiscountAmount;
                currentBill.value.selectionTotalDiscountsAndVouchersAmount =
                    selectionTotals.value.selectionTotalDiscountsAndVouchersAmount;
                currentBill.value.selectionTotalAfterItemDiscounts =
                    selectionTotals.value.selectionTotalAfterItemDiscounts;
                currentBill.value.selectionTotalAfterDiscount = selectionTotals.value.selectionTotalAfterDiscount;
            }

            const bill = await customerDao.getCurrentBill({ billId: currentOrderBillId });
            if (currentBill.value && bill) {
                currentBill.value.appliedItemDiscountSummaries = bill.appliedItemDiscountSummaries;
                currentBill.value.appliedBillDiscountSummaries = bill.appliedBillDiscountSummaries;
                if (
                    bill.selectionDiscountAmount !== selectionTotals.value.selectionDiscountAmount ||
                    bill.selectionTotalAfterItemDiscounts !== selectionTotals.value.selectionTotalAfterItemDiscounts ||
                    bill.selectionTotalDiscountsAndVouchersAmount !==
                        selectionTotals.value.selectionTotalDiscountsAndVouchersAmount ||
                    bill.selectionTotalAfterDiscount !== selectionTotals.value.selectionTotalAfterDiscount
                ) {
                    // Popup.show({ message: "Totals do not match!" });
                    console.log(
                        "Bill totals:",
                        bill.selectionDiscountAmount,
                        bill.selectionTotalAfterItemDiscounts,
                        bill.selectionTotalDiscountsAndVouchersAmount,
                        bill.selectionTotalAfterDiscount
                    );
                    console.log(
                        "Selection totals:",
                        selectionTotals.value.selectionDiscountAmount,
                        selectionTotals.value.selectionTotalAfterItemDiscounts,
                        selectionTotals.value.selectionTotalDiscountsAndVouchersAmount,
                        selectionTotals.value.selectionTotalAfterDiscount
                    );
                    return;
                }
            }
        } catch (ex) {
            logError(ex);
        }
    }

    function setCurrentBill(newBill: IBill | null) {
        // todo: workaround for faster view refresh
        // there is a delay between cancelling items and closing bill
        // so there is a moment where there are no items to pay, but the bill is still not closed
        // here we simulate closed bill if there are no items to pay - because it will be closed within a few seconds
        if (newBill?.unpaidItems.length == 0) {
            newBill = null;
        }

        if (newBill == null) {
            currentBill.value = null;
            unpaidItems.value = [];
            paidItems.value = [];
            return;
        }

        const identityId = identity.identityId ?? "";
        currentBill.value = newBill;
        unpaidItems.value = Bill.getUnpaidLineItems(newBill, identityId);
        paidItems.value = Bill.getPaidLineItems(newBill);
    }

    // Only an item of a price per minute article is priced by a clock, so that is checked first
    type RunningTimerItem = IBillItem & { timerStartedDateTime: Date; timerMinuteRate: number };
    function isRunningTimerItem(item: IBillItem): item is RunningTimerItem {
        return (
            item.isPricePerMinute &&
            item.timerMinuteRate !== undefined &&
            item.timerStartedDateTime !== undefined &&
            item.timerStoppedDateTime === undefined
        );
    }

    // The unit price of an item, a running timer is priced along with its clock, which counts on from the elapsed time the
    // server told
    function getLiveUnitPrice(item: IBillItem): number {
        if (!isRunningTimerItem(item)) return item.unitPrice;
        if (currentBill.value == null) return item.unitPrice;

        const elapsedSeconds = getTimerElapsedSeconds(
            item.timerElapsedSeconds,
            item.timerElapsedReadDateTime,
            true,
            timersNow.now.value
        );

        return calculateTimerPrice(item.timerMinuteRate, elapsedSeconds);
    }

    function getLiveUnitPriceWithOptions(item: IBillItem): number {
        if (!isRunningTimerItem(item)) return item.unitPriceWithOptions;

        return item.unitPriceWithOptions - item.unitPrice + getLiveUnitPrice(item);
    }

    // The unit price of an item after its item discounts. Those of a running timer follow its price, so they are worked out
    // from the live price like the server does; the bill-level discounts and vouchers are fixed amounts, which do not move
    function getLiveUnitPriceAfterDiscount(item: IBillItem): number {
        if (!isRunningTimerItem(item)) return item.unitPriceAfterDiscount;

        return applyTimerDiscounts(getLiveUnitPrice(item), item.timerDiscounts);
    }

    function getSelectedItemsOriginalConsumption(selectedItems: UnpaidLineItem[]) {
        let selectedItemsTotalOriginalConsumption = 0;
        for (const selectedItem of selectedItems) {
            selectedItemsTotalOriginalConsumption += selectedItem.selectedQuantity * getLiveUnitPriceWithOptions(selectedItem);
        }

        return selectedItemsTotalOriginalConsumption;
    }

    async function selectItem(item: UnpaidLineItem, quantity: number, sendToServer: boolean = true) {
        const unpaidItem = unpaidItems.value.find((x) => x.id === item.id);
        if (unpaidItem == null) return;

        unpaidItem.selectedQuantity = quantity;

        if (sendToServer) {
            await sendSelectedItemsToServer();
        }
    }

    async function selectAllItems() {
        for (const unpaidItem of unpaidItems.value) {
            unpaidItem.selectedQuantity = unpaidItem.unpaidQuantity;
        }

        await sendSelectedItemsToServer();
    }

    async function deselectAllItems() {
        for (const unpaidItem of unpaidItems.value) {
            unpaidItem.selectedQuantity = 0;
        }

        await sendSelectedItemsToServer();
    }

    async function sendSelectedItemsToServer(newSelection: ISelectedItem[] | null = null) {
        const selectionToSend = newSelection ?? selectedItemsList.value;

        if (!hasUnpaidItems.value) return;

        const billId = currentBill.value?.id;
        if (billId == null) return;

        try {
            isUpdating.value = true;

            const { appSocket } = useAppSocket();

            const selectItemsToPayResult = await appSocket.selectItemsToPay({
                selectedItems: selectionToSend,
                billId: billId,
            });

            if (!selectItemsToPayResult.isSuccess) {
                Popup.show({
                    message: translations.cart.changingTotalWillRemoveAppliedVouchersMessage,
                    confirmHandler: async () => {
                        try {
                            await customerDao.removeAllBillVouchers({ billId: billId });
                            sendSelectedItemsToServer(selectionToSend);
                            loadCurrentBill();
                        } catch (err) {
                            logError(err);
                            Popup.show({ message: trans.general.error });
                        }
                    },
                    confirmButton: translations.cart.removeVouchers,
                    cancelButton: translations.general.cancel,
                    uniqueId: "changingTotalWillRemoveAppliedVouchersPopup",
                    highlightConfirm: false,
                    highlightCancel: true,
                    cancelHandler: () => {
                        loadCurrentBill();
                    },
                });
            } else {
                // todo: temporary solution, should be better and more performant
                updateBillTotals();
            }
        } finally {
            isUpdating.value = false;
        }
    }

    function updateBillName(billId: string, name: string) {
        const currentBillId = currentBill.value?.id;
        if (currentBill.value && currentBillId != billId) {
            return;
        }

        currentBill.value!.customName = name;
    }

    function updateBillManualEndUsersCount(billId: string, manualEndUsersCount: number) {
        const currentBillId = currentBill.value?.id;
        if (currentBill.value && currentBillId != billId) {
            return;
        }

        currentBill.value!.manualEndUsersCount = manualEndUsersCount;
    }

    // event handlers
    function updateSelectedItems(selections: IUserItemSelection[]) {
        if (currentBill.value) {
            currentBill.value.selections = selections;
        }

        // update selected items quantity
        for (const selection of selections) {
            const unpaidItem = unpaidItems.value.find((item) => item.id === selection.billItemId);
            if (unpaidItem == null) continue;

            unpaidItem.selectedQuantity = selection.quantity;
        }

        // unselect not selected items
        for (const existingSelections of selectedUnpaidItems.value) {
            const unpaidItem = selections.find((item) => item.billItemId === existingSelections.id);
            if (unpaidItem == null) {
                existingSelections.selectedQuantity = 0;
            }
        }

        // todo: temp until we figure out better solution
        if (currentBill.value?.hasDiscount) {
            updateBillTotals();
        }
    }

    function isVouchersAppliedToBillException(error: any) {
        return error instanceof DaoError && error.type === exceptionTypes.vouchersAppliedToBillException;
    }

    function showConfirmItemDiscountsRemovalPopup(item: IBillItem | IBillItemOption) {
        Popup.show({
            message: translations.discounts.removeAllItemDiscountsPrompt,
            confirmHandler: async () => {
                try {
                    const newBill = await customerDao.removeBillItemDiscounts(currentBill.value!.id, item.id, {
                        socketConnectionId: undefined,
                    });
                    setCurrentBill(newBill);
                } catch (error) {
                    logError(error);
                    handleError(error, translations.general.operationFailed);
                }
            },
            confirmButton: translations.general.remove,
            cancelButton: translations.general.cancel,
            highlightConfirm: false,
            highlightCancel: true,
            confirmBtnOnTheLeft: false,
            uniqueId: "confirmBillItemDiscountsRemovalPopup",
        });
    }

    function handleError(error: any, defaultMsg: string) {
        if (error instanceof DaoError) {
            switch (error.type) {
                case exceptionTypes.paymentInProgressException:
                    Popup.show({
                        message: translations.cart.paymentBlockingCart,
                    });
                    break;
                case exceptionTypes.openPriceIsMissingForArticleToCartException:
                case exceptionTypes.articleCannotBeAddedToCartException:
                    Popup.show({
                        message: translations.cart.failedToAdd,
                    });
                    break;

                default:
                    if (isVouchersAppliedToBillException(error)) {
                        // showConfirmDiscountsRemovalPopup();
                        console.error("Vouchers applied to bill - not implemented yet");
                    }
                    break;
            }
        } else {
            Popup.show({ message: defaultMsg });
        }
    }

    return {
        // state
        isLoading,
        isUpdating,
        unpaidItems,
        paidItems,
        currentBill,

        // getters
        locationId,
        consumption,
        totalPayableAfterDiscount,
        totalItemsSelected,
        totalPaid,
        hasUnpaidItems,
        hasPaidItems,
        isAnyItemSelected,
        selectedItemsList,
        isOpenBill,
        totalDiscountsAndVouchersAmount,
        isFullyPaid,
        totalUnpaidConsumption,
        unpaidAfterDiscounts,
        isFullyCoveredByDiscounts,
        hasVouchers,
        hasDiscounts,

        // actions
        loadCurrentBill,
        setCurrentBill,
        selectItem,
        selectAllItems,
        deselectAllItems,
        updateSelectedItems,
        updateBillName,
        updateBillManualEndUsersCount,
        showConfirmItemDiscountsRemovalPopup,
        getAppliedBillDiscount,
        getLiveUnitPrice,
        getLiveUnitPriceAfterDiscount,
    };
});

export type BillStore = ReturnType<typeof useBillStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useBillStore, import.meta.hot));
}
